import React, { useState } from 'react';
import {
  Box, Grid, TextField, Typography, Accordion, AccordionSummary, AccordionDetails,
  Autocomplete, Chip, MenuItem, FormControlLabel, Checkbox, Switch, FormGroup,
  InputAdornment
} from '@mui/material';
import { ChevronDown, Hash } from 'lucide-react';

/**
 * Shared, presentation-layer form for the enterprise NextGen Builder project
 * definition. Used identically by the "Add New Project" and "Edit Project"
 * dialogs so the two never drift apart.
 *
 * Contract: `formData` is a flat object keyed by the exact ngb_projects
 * column names the backend understands (see PROJECT_FIELDS in
 * projectController.js). `onChange(field, value)` sets a single field.
 */

// ── Option lists (curated — every Autocomplete below is also freeSolo) ─────

export const CATEGORY_OPTIONS = [
  'Web Development', 'Mobile Development', 'AI/ML', 'Cloud', 'DevOps',
  'Cyber Security', 'Data Engineering', 'Full Stack', 'Embedded', 'Blockchain', 'Other'
];

export const DIFFICULTY_OPTIONS = ['Beginner', 'Intermediate', 'Advanced'];

export const DURATION_UNIT_OPTIONS = ['Days', 'Weeks', 'Months'];

export const SKILL_OPTIONS = [
  'Java Basics', 'HTML', 'CSS', 'React', 'Node', 'Git', 'SQL', 'Docker', 'Linux',
  'Python', 'JavaScript', 'TypeScript', 'REST APIs', 'Data Structures', 'Cloud Basics'
];

export const PROGRAMMING_LANGUAGE_OPTIONS = [
  'Java', 'Python', 'JavaScript', 'TypeScript', 'Go', 'C#', 'C++', 'Kotlin', 'Swift', 'Ruby', 'PHP', 'Rust'
];

export const FRONTEND_OPTIONS = ['React', 'Angular', 'Vue.js', 'Next.js', 'Svelte', 'Flutter', 'React Native'];
export const BACKEND_OPTIONS = ['Node.js', 'Express.js', 'Spring Boot', 'Django', 'FastAPI', 'NestJS', '.NET Core', 'Laravel'];
export const DATABASE_TECH_OPTIONS = ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'DynamoDB', 'SQLite', 'Cassandra'];
export const CLOUD_OPTIONS = ['AWS', 'Azure', 'GCP', 'DigitalOcean', 'Vercel', 'Firebase'];
export const CICD_OPTIONS = ['GitHub Actions', 'Jenkins', 'Docker', 'Kubernetes', 'GitLab CI', 'CircleCI'];
export const AI_TOOL_OPTIONS = ['OpenAI', 'Claude', 'Cursor', 'GitHub Copilot', 'Gemini'];

export const BRANCH_STRATEGY_OPTIONS = ['main', 'develop', 'feature/*'];
export const GITHUB_VISIBILITY_OPTIONS = ['Private', 'Public'];

export const DELIVERABLE_OPTIONS = [
  'Architecture Diagram', 'README', 'API Documentation', 'Unit Tests',
  'Deployment', 'Demo Video', 'Presentation', 'Retrospective'
];

export const ENROLLMENT_TYPE_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'approval_required', label: 'Approval Required' },
  { value: 'invite_only', label: 'Invite Only' }
];

export const ROLE_OPTIONS = [
  'Frontend Developer', 'Backend Developer', 'Full Stack Developer', 'Mobile Developer',
  'DevOps Engineer', 'QA Engineer', 'UI/UX Designer', 'AI Engineer', 'Data Engineer', 'Cloud Engineer'
];

export const STATUS_OPTIONS = ['active', 'draft', 'inactive', 'archived'];

/** Default shape for a brand-new project form — spread this into state. */
export const DEFAULT_PROJECT_FORM = {
  // Basic Information
  name: '', description: '', vision: '', project_summary: '', category: '', difficulty_level: '',
  estimated_duration: '', duration_unit: 'Days', total_sprints: '', estimated_versions: '', tags: '', status: 'active',
  // Purpose (stored in the details JSONB overflow)
  problem_statement: '', business_value: '', student_benefits: '', career_benefits: '',
  third_party_integrations: [],
  // Learning Information
  learning_objectives: '', prerequisites: [], recommended_skills: [], expected_outcomes: '',
  // Technology Stack
  tech_stack_id: '', tech_stacks: [], languages: [], primary_language: '', framework: '', database_type: '', architecture: '',
  frontend_tech: [], backend_tech: [], database_tech: [], cloud_platforms: [], cicd_tools: [], ai_tools: [],
  // Timeline
  start_date: '', end_date: '', enrollment_opens: '', enrollment_closes: '', expected_completion: '',
  // Team
  default_team_size: 25, min_team_size: '', max_team_size: '', mentor_capacity: '', max_concurrent_teams: '',
  // Repository
  repository_url: '', github_organization: '', default_branch: 'main', branch_strategy: 'main', github_visibility: 'Private', repository_template: false,
  // Evaluation
  passing_score: '', min_pr_approval: '', min_ai_review_score: '', code_coverage_requirement: '',
  mandatory_ai_review: false, mandatory_peer_review: false,
  // Deliverables
  deliverables: [],
  // Certificates
  completion_certificate: true, excellence_badge: false, industry_recommendation: false, linkedin_badge: false,
  // Enrollment Configuration
  enrollment_type: 'open', preferred_role_selection: true, preferred_roles: [], allow_multiple_roles: false,
  // Assets
  logo_url: '', thumbnail_url: '', banner_url: '', architecture_diagram_url: '', demo_video_url: '', documentation_url: '', community_link: ''
};

// ── Small building blocks ───────────────────────────────────────────────────

const Section = ({ title, subtitle, defaultExpanded = false, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      elevation={0}
      disableGutters
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: '12px !important',
        mb: 1.5,
        overflow: 'hidden',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDown size={18} color="#64748b" />}
        sx={{ bgcolor: '#f8fafc', px: 2.5, py: 0.5, '& .MuiAccordionSummary-content': { my: 1.25 } }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{subtitle}</Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2.5, bgcolor: '#fff' }}>
        <Grid container spacing={2}>
          {children}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const Field = ({ half = true, children }) => (
  <Grid item xs={12} sm={half ? 6 : 12}>{children}</Grid>
);

const MultiSelect = ({ label, value, options, onChange, placeholder, helperText, color = { bg: '#eef2ff', fg: '#4f46e5' } }) => (
  <Autocomplete
    multiple
    freeSolo
    options={options}
    value={value || []}
    onChange={(_, newValue) => onChange(newValue)}
    renderTags={(val, getTagProps) =>
      val.map((option, index) => (
        <Chip label={option} size="small" {...getTagProps({ index })} sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600 }} />
      ))
    }
    renderInput={(params) => (
      <TextField {...params} label={label} placeholder={placeholder || 'Select or type, press Enter to add'} helperText={helperText} />
    )}
  />
);

const YesNo = ({ label, checked, onChange, helperText }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
    <FormControlLabel
      control={<Switch checked={!!checked} onChange={(e) => onChange(e.target.checked)} />}
      label={<Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>{label}</Typography>}
    />
    {helperText && <Typography variant="caption" sx={{ color: '#94a3b8', ml: 1.5 }}>{helperText}</Typography>}
  </Box>
);

// ── Main component ──────────────────────────────────────────────────────────

const ProjectFormSections = ({ formData, onChange, techStacks = [], isEdit = false, totalVersionsComputed }) => {
  const set = (field) => (value) => onChange(field, value);
  const setEvt = (field) => (e) => onChange(field, e.target.value);
  const setChecked = (field) => (e) => onChange(field, e.target.checked);

  return (
    <Box>
      {/* ── 1. Basic Information ─────────────────────────────────────────── */}
      <Section title="1. Basic Information" subtitle="Identity, summary, and classification" defaultExpanded>
        {isEdit && (
          <Field half>
            <TextField
              label="Project Code" fullWidth disabled
              value={formData.project_code || 'Generated on save'}
              InputProps={{ startAdornment: <InputAdornment position="start"><Hash size={14} /></InputAdornment> }}
              helperText="Auto-generated — cannot be edited."
            />
          </Field>
        )}
        <Field half={isEdit}>
          <TextField label="Project Name" required fullWidth value={formData.name || ''} onChange={setEvt('name')} placeholder="e.g. Nova" />
        </Field>
        <Field>
          <TextField
            label="Project Summary" required fullWidth multiline rows={2}
            value={formData.project_summary || ''} onChange={setEvt('project_summary')}
            placeholder="Short 2–3 line summary students see in the catalogue."
          />
        </Field>
        <Field>
          <TextField
            label="Description" fullWidth multiline rows={3}
            value={formData.description || ''} onChange={setEvt('description')}
          />
        </Field>
        <Field>
          <TextField
            label="Vision" fullWidth multiline rows={2}
            value={formData.vision || ''} onChange={setEvt('vision')}
            placeholder="e.g. To build a unified digital ecosystem for students worldwide."
            helperText="The long-term goal shown to students on the enrollment page."
          />
        </Field>
        <Field half>
          <TextField select label="Category" required fullWidth value={formData.category || ''} onChange={setEvt('category')}>
            {CATEGORY_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Field>
        <Field half>
          <TextField select label="Difficulty Level" required fullWidth value={formData.difficulty_level || ''} onChange={setEvt('difficulty_level')}>
            {DIFFICULTY_OPTIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
        </Field>
        <Grid item xs={8} sm={4}>
          <TextField
            label="Estimated Duration" required type="number" fullWidth
            value={formData.estimated_duration || ''} onChange={setEvt('estimated_duration')} placeholder="45"
          />
        </Grid>
        <Grid item xs={4} sm={2}>
          <TextField select label="Unit" fullWidth value={formData.duration_unit || 'Days'} onChange={setEvt('duration_unit')}>
            {DURATION_UNIT_OPTIONS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Total Sprints" type="number" fullWidth value={formData.total_sprints || ''} onChange={setEvt('total_sprints')} placeholder="8" />
        </Grid>
        <Field half>
          <TextField
            label="Total Versions" fullWidth disabled
            value={isEdit ? (totalVersionsComputed ?? 0) : 'Created after saving'}
            helperText="Auto-calculated from the versions generated for this project."
          />
        </Field>
        <Field half>
          <TextField
            label="Estimated Versions to Auto-Create" type="number" fullWidth
            value={formData.estimated_versions || ''} onChange={setEvt('estimated_versions')}
            helperText={isEdit ? 'Only used when the project was first created.' : 'Creates this many versions (with team assignments) on save.'}
          />
        </Field>
        <Field half>
          <TextField select label="Status" fullWidth value={formData.status || 'active'} onChange={setEvt('status')}>
            {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
          </TextField>
        </Field>
        <Field half>
          <TextField label="Tags (comma separated)" fullWidth value={formData.tags || ''} onChange={setEvt('tags')} />
        </Field>
      </Section>

      {/* ── 2. Learning Details ──────────────────────────────────────────── */}
      <Section title="2. Learning Details" subtitle="What students will learn and achieve">
        <Field>
          <TextField
            label="Learning Objectives" required fullWidth multiline rows={4}
            value={formData.learning_objectives || ''} onChange={setEvt('learning_objectives')}
            placeholder={'Students will learn\n• Git\n• Agile\n• Backend APIs\n• Database Design\n• CI/CD\n• Cloud Deployment'}
          />
        </Field>
        <Field>
          <MultiSelect
            label="Prerequisites" value={formData.prerequisites} options={SKILL_OPTIONS}
            onChange={set('prerequisites')} color={{ bg: '#fff7ed', fg: '#c2410c' }}
          />
        </Field>
        <Field>
          <MultiSelect
            label="Recommended Skills" value={formData.recommended_skills} options={SKILL_OPTIONS}
            onChange={set('recommended_skills')} color={{ bg: '#f0fdf4', fg: '#166534' }}
          />
        </Field>
        <Field>
          <TextField
            label="Expected Outcomes" fullWidth multiline rows={4}
            value={formData.expected_outcomes || ''} onChange={setEvt('expected_outcomes')}
            placeholder={'By completing this project students should be able to\nBuild enterprise applications\nDeploy to cloud\nWork in Agile\nCreate production-ready pull requests'}
          />
        </Field>
      </Section>

      {/* ── 3. Technology Stack ──────────────────────────────────────────── */}
      <Section title="3. Technology Stack" subtitle="Languages, frameworks, infrastructure and AI tooling">
        <Field>
          <MultiSelect
            label="Tech Stacks" value={formData.tech_stacks}
            options={[...new Set(techStacks.map((s) => s.name))]}
            onChange={(nv) => {
              const matched = techStacks.find((s) => nv.some((v) =>
                String(v).toLowerCase().includes(String(s.name).toLowerCase()) ||
                String(s.name).toLowerCase().includes(String(v).toLowerCase())
              ));
              onChange('tech_stacks', nv);
              onChange('tech_stack_id', matched?.id || formData.tech_stack_id || '');
            }}
            helperText="Multiple allowed — select from the list or type your own."
          />
        </Field>
        <Field>
          <MultiSelect
            label="Programming Languages" value={formData.languages} options={PROGRAMMING_LANGUAGE_OPTIONS}
            onChange={set('languages')} color={{ bg: '#f0fdf4', fg: '#166534' }}
            helperText="Multiple allowed — first one becomes the primary language."
          />
        </Field>
        <Field half>
          <TextField label="Framework" fullWidth value={formData.framework || ''} onChange={setEvt('framework')} />
        </Field>
        <Field half>
          <TextField label="Database" fullWidth value={formData.database_type || ''} onChange={setEvt('database_type')} />
        </Field>
        <Field>
          <TextField label="Architecture" fullWidth value={formData.architecture || ''} onChange={setEvt('architecture')} />
        </Field>
        <Field half>
          <MultiSelect label="Frontend" value={formData.frontend_tech} options={FRONTEND_OPTIONS} onChange={set('frontend_tech')} />
        </Field>
        <Field half>
          <MultiSelect label="Backend" value={formData.backend_tech} options={BACKEND_OPTIONS} onChange={set('backend_tech')} />
        </Field>
        <Field half>
          <MultiSelect label="Database Technologies" value={formData.database_tech} options={DATABASE_TECH_OPTIONS} onChange={set('database_tech')} />
        </Field>
        <Field half>
          <MultiSelect label="Cloud" value={formData.cloud_platforms} options={CLOUD_OPTIONS} onChange={set('cloud_platforms')} color={{ bg: '#eff6ff', fg: '#1d4ed8' }} />
        </Field>
        <Field half>
          <MultiSelect label="CI/CD" value={formData.cicd_tools} options={CICD_OPTIONS} onChange={set('cicd_tools')} color={{ bg: '#f5f3ff', fg: '#6d28d9' }} />
        </Field>
        <Field half>
          <MultiSelect label="AI Tools" value={formData.ai_tools} options={AI_TOOL_OPTIONS} onChange={set('ai_tools')} color={{ bg: '#fdf2f8', fg: '#be185d' }} />
        </Field>
      </Section>

      {/* ── 4. Timeline ───────────────────────────────────────────────────── */}
      <Section title="4. Timeline" subtitle="Key dates for the program">
        {[
          ['start_date', 'Start Date'], ['end_date', 'End Date'],
          ['enrollment_opens', 'Enrollment Opens'], ['enrollment_closes', 'Enrollment Closes'],
          ['expected_completion', 'Expected Completion'],
        ].map(([field, label]) => (
          <Field half key={field}>
            <TextField
              label={label} type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={formData[field] || ''} onChange={setEvt(field)}
            />
          </Field>
        ))}
      </Section>

      {/* ── 5. Team Configuration ────────────────────────────────────────── */}
      <Section title="5. Team Configuration" subtitle="Sizing and mentor capacity">
        <Field half>
          <TextField label="Default Team Size" type="number" fullWidth value={formData.default_team_size ?? 25} onChange={setEvt('default_team_size')} />
        </Field>
        <Grid item xs={6} sm={3}>
          <TextField label="Min Team Size" type="number" fullWidth value={formData.min_team_size || ''} onChange={setEvt('min_team_size')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Max Team Size" type="number" fullWidth value={formData.max_team_size || ''} onChange={setEvt('max_team_size')} />
        </Grid>
        <Field half>
          <TextField label="Mentor Capacity" type="number" fullWidth value={formData.mentor_capacity || ''} onChange={setEvt('mentor_capacity')} helperText="Students a single mentor can support." />
        </Field>
        <Field half>
          <TextField label="Maximum Concurrent Teams" type="number" fullWidth value={formData.max_concurrent_teams || ''} onChange={setEvt('max_concurrent_teams')} />
        </Field>
      </Section>

      {/* ── 6. Repository ─────────────────────────────────────────────────── */}
      <Section title="6. Repository" subtitle="GitHub configuration for this project">
        <Field>
          <TextField
            label="Repository URL" fullWidth placeholder="https://github.com/evolvexinnovations/nova"
            value={formData.repository_url || ''} onChange={setEvt('repository_url')}
            helperText="GitHub repository students get invited to."
          />
        </Field>
        <Field half>
          <TextField label="GitHub Organization" fullWidth value={formData.github_organization || ''} onChange={setEvt('github_organization')} placeholder="evolvexinnovations" />
        </Field>
        <Field half>
          <TextField label="Default Branch" fullWidth value={formData.default_branch || 'main'} onChange={setEvt('default_branch')} />
        </Field>
        <Field half>
          <TextField select label="Branch Strategy" fullWidth value={formData.branch_strategy || 'main'} onChange={setEvt('branch_strategy')}>
            {BRANCH_STRATEGY_OPTIONS.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </TextField>
        </Field>
        <Field half>
          <TextField select label="GitHub Visibility" fullWidth value={formData.github_visibility || 'Private'} onChange={setEvt('github_visibility')}>
            {GITHUB_VISIBILITY_OPTIONS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
        </Field>
        <Field half>
          <YesNo label="Repository Template" checked={formData.repository_template} onChange={set('repository_template')} helperText="Provision each team's repo from a starter template." />
        </Field>
      </Section>

      {/* ── 7. Evaluation ─────────────────────────────────────────────────── */}
      <Section title="7. Evaluation" subtitle="Grading thresholds and review requirements">
        <Grid item xs={6} sm={3}>
          <TextField label="Passing Score (%)" type="number" fullWidth value={formData.passing_score || ''} onChange={setEvt('passing_score')} placeholder="80" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Min PR Approvals" type="number" fullWidth value={formData.min_pr_approval || ''} onChange={setEvt('min_pr_approval')} placeholder="2" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Min AI Review Score (%)" type="number" fullWidth value={formData.min_ai_review_score || ''} onChange={setEvt('min_ai_review_score')} placeholder="85" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Code Coverage (%)" type="number" fullWidth value={formData.code_coverage_requirement || ''} onChange={setEvt('code_coverage_requirement')} placeholder="70" />
        </Grid>
        <Field half>
          <FormControlLabel
            control={<Checkbox checked={!!formData.mandatory_ai_review} onChange={setChecked('mandatory_ai_review')} />}
            label="Mandatory AI Review"
          />
        </Field>
        <Field half>
          <FormControlLabel
            control={<Checkbox checked={!!formData.mandatory_peer_review} onChange={setChecked('mandatory_peer_review')} />}
            label="Mandatory Peer Review"
          />
        </Field>
      </Section>

      {/* ── 8. Deliverables ───────────────────────────────────────────────── */}
      <Section title="8. Deliverables" subtitle="What students must complete">
        <Grid item xs={12}>
          <FormGroup row>
            {DELIVERABLE_OPTIONS.map((d) => (
              <FormControlLabel
                key={d}
                sx={{ width: { xs: '100%', sm: '48%' } }}
                control={
                  <Checkbox
                    checked={(formData.deliverables || []).includes(d)}
                    onChange={(e) => {
                      const cur = formData.deliverables || [];
                      onChange('deliverables', e.target.checked ? [...cur, d] : cur.filter((x) => x !== d));
                    }}
                  />
                }
                label={d}
              />
            ))}
          </FormGroup>
        </Grid>
      </Section>

      {/* ── 9. Certificates ───────────────────────────────────────────────── */}
      <Section title="9. Certificates" subtitle="Recognition granted on completion">
        {[
          ['completion_certificate', 'Completion Certificate'],
          ['excellence_badge', 'Excellence Badge'],
          ['industry_recommendation', 'Industry Recommendation'],
          ['linkedin_badge', 'LinkedIn Badge'],
        ].map(([field, label]) => (
          <Field half key={field}>
            <FormControlLabel
              control={<Checkbox checked={!!formData[field]} onChange={setChecked(field)} />}
              label={label}
            />
          </Field>
        ))}
      </Section>

      {/* ── 10. Enrollment Settings ──────────────────────────────────────── */}
      <Section title="10. Enrollment Settings" subtitle="How students join this project">
        <Field half>
          <TextField select label="Enrollment Type" fullWidth value={formData.enrollment_type || 'open'} onChange={setEvt('enrollment_type')}>
            {ENROLLMENT_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Field>
        <Field half>
          <YesNo
            label="Preferred Role Selection" checked={formData.preferred_role_selection}
            onChange={set('preferred_role_selection')} helperText="Let students pick a role while enrolling."
          />
        </Field>
        {formData.preferred_role_selection && (
          <Field>
            <MultiSelect
              label="Role List" value={formData.preferred_roles} options={ROLE_OPTIONS}
              onChange={set('preferred_roles')} color={{ bg: '#ecfeff', fg: '#0e7490' }}
            />
          </Field>
        )}
        <Field half>
          <YesNo label="Allow Multiple Roles" checked={formData.allow_multiple_roles} onChange={set('allow_multiple_roles')} />
        </Field>
      </Section>

      {/* ── 11. Assets ────────────────────────────────────────────────────── */}
      <Section title="11. Assets" subtitle="Media and links shown on the project page">
        <Field half>
          <TextField label="Logo URL" fullWidth value={formData.logo_url || ''} onChange={setEvt('logo_url')} />
        </Field>
        <Field half>
          <TextField label="Project Thumbnail URL" fullWidth value={formData.thumbnail_url || ''} onChange={setEvt('thumbnail_url')} />
        </Field>
        <Field half>
          <TextField label="Project Banner URL" fullWidth value={formData.banner_url || ''} onChange={setEvt('banner_url')} />
        </Field>
        <Field half>
          <TextField label="Architecture Diagram URL" fullWidth value={formData.architecture_diagram_url || ''} onChange={setEvt('architecture_diagram_url')} />
        </Field>
        <Field half>
          <TextField label="Demo Video URL" fullWidth value={formData.demo_video_url || ''} onChange={setEvt('demo_video_url')} />
        </Field>
        <Field half>
          <TextField label="Documentation URL" fullWidth value={formData.documentation_url || ''} onChange={setEvt('documentation_url')} />
        </Field>
        <Field>
          <TextField label="Slack / Discord Link" fullWidth value={formData.community_link || ''} onChange={setEvt('community_link')} />
        </Field>
      </Section>
    </Box>
  );
};

export default ProjectFormSections;
